import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@redpanda/ui';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Textarea, CustomSelect } from '@redpanda/ui';
import FormFieldWithError from './forms/FormFieldWithError';
import { createInvoice, updateInvoice } from '../services/api';
import { toast } from 'react-hot-toast';
import { useInvoiceStoreBase } from '../stores/invoiceStore';

interface InvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: any;
  onInvoiceCreated: (savedInvoice?: any, isEdit?: boolean) => void;
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  isOpen,
  onClose,
  invoice,
  onInvoiceCreated
}) => {
  const { t } = useTranslation();

  const invoiceItemSchema = useMemo(() => z.object({
    description: z.string().min(1, t('common.forms.validation.required')),
    quantity: z.coerce.number().min(0.01, t('common.form.minQuantity')),
    unitPrice: z.coerce.number().min(0.01, t('common.form.minUnitPrice')),
  }), [t]);

  const invoiceSchema = useMemo(() => z.object({
    number: z.string().min(1, t('common.forms.validation.required')),
    date: z.string().min(1, t('common.forms.validation.required')),
    dueDate: z.string().optional(),
    customerName: z.string().min(1, t('common.forms.validation.required')),
    customerAddress: z.string().optional(),
    customerTaxId: z.string().optional(),
    taxRate: z.coerce.number().min(0).max(1).optional(),
    status: z.enum(['DRAFT', 'SENT', 'PAID', 'OVERDUE', 'CANCELLED']).optional(),
    notes: z.string().optional(),
    items: z.array(invoiceItemSchema).min(1, t('common.forms.validation.required')),
  }), [t, invoiceItemSchema]);

  type InvoiceFormData = z.infer<typeof invoiceSchema>;
  const isEditing = !!invoice;

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    shouldFocusError: true, // 確保自動捲動到錯誤欄位
    defaultValues: {
      number: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      customerName: '',
      customerAddress: '',
      customerTaxId: '',
      taxRate: 0.05,
      status: 'DRAFT',
      notes: '',
      items: [
        { description: '', quantity: 1, unitPrice: '' as any }
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = watch('items');

  // 計算總計
  const calculateTotals = () => {
    const subtotal = watchedItems.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    const taxRate = watch('taxRate') || 0;
    const taxAmount = subtotal * taxRate;
    const totalAmount = subtotal + taxAmount;

    return { subtotal, taxAmount, totalAmount };
  };

  const { subtotal, taxAmount, totalAmount } = calculateTotals();

  useEffect(() => {
    if (isOpen) {
      if (isEditing && invoice) {
        // 編輯模式：填入現有資料
        reset({
          number: invoice.number || '',
          date: invoice.date ? invoice.date.split('T')[0] : new Date().toISOString().split('T')[0],
          dueDate: invoice.dueDate ? invoice.dueDate.split('T')[0] : '',
          customerName: invoice.customerName || '',
          customerAddress: invoice.customerAddress || '',
          customerTaxId: invoice.customerTaxId || '',
          taxRate: invoice.taxRate || 0.05,
          status: invoice.status || 'DRAFT',
          notes: invoice.notes || '',
          items: invoice.items && invoice.items.length > 0 
            ? invoice.items.map((item: any) => ({
                description: item.description || '',
                quantity: item.quantity || 1,
                unitPrice: item.unitPrice || '',
              }))
            : [{ description: '', quantity: 1, unitPrice: '' as any }]
        });
      } else {
        // 新增模式：重置表單
        reset({
          number: '',
          date: new Date().toISOString().split('T')[0],
          dueDate: '',
          customerName: '',
          customerAddress: '',
          customerTaxId: '',
          taxRate: 0.05,
          status: 'DRAFT',
          notes: '',
          items: [{ description: '', quantity: 1, unitPrice: '' as any }],
        });
      }
    }
  }, [isOpen, isEditing, invoice, reset]);

  const handleAddItem = () => {
    append({ description: '', quantity: 1, unitPrice: '' as any });
  };

  const handleRemoveItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = async (data: InvoiceFormData) => {
    const submitData = {
      ...data,
      date: new Date(data.date).toISOString(),
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined,
    };

    if (isEditing) {
      // UPDATE 樂觀更新
      const originalInvoice = { ...invoice };
      const optimisticInvoice = { ...invoice, ...submitData, updatedAt: new Date().toISOString() };

      useInvoiceStoreBase.getState().updateItem(invoice.id, optimisticInvoice);
      onClose();

      try {
        const response = await updateInvoice(invoice.id, submitData);
        const realData = response.data || optimisticInvoice;
        useInvoiceStoreBase.getState().updateItem(invoice.id, realData);
        toast.success(t('common.messages.updateSuccess'));
        onInvoiceCreated(realData, true);
      } catch (error: any) {
        useInvoiceStoreBase.getState().updateItem(invoice.id, originalInvoice);
        const msg = error.response?.data?.error || error.response?.data?.message || t('common.form.operationFailed');
        toast.error(msg);
      }
    } else {
      // CREATE 樂觀更新
      const tempId = `temp-${Date.now()}`;
      const tempInvoice = {
        id: tempId,
        number: submitData.number,
        invoiceNumber: submitData.number,
        date: submitData.date,
        issueDate: submitData.date,
        dueDate: submitData.dueDate,
        customerName: submitData.customerName,
        customerAddress: submitData.customerAddress,
        customerTaxId: submitData.customerTaxId,
        taxRate: submitData.taxRate || 0.05,
        subtotal: Math.round(subtotal),
        taxAmount: Math.round(taxAmount),
        totalAmount: Math.round(totalAmount),
        status: (submitData.status || 'DRAFT') as any,
        notes: submitData.notes,
        items: (data.items || []).map((item: any, idx: number) => ({
          id: `temp-item-${idx}`,
          description: item.description,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          amount: Math.round(Number(item.quantity) * Number(item.unitPrice)),
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      useInvoiceStoreBase.getState().addItem(tempInvoice as any);
      onClose();

      try {
        const response = await createInvoice(submitData);
        const realData = response.data || tempInvoice;
        useInvoiceStoreBase.getState().updateItem(tempId, realData);
        toast.success(t('common.messages.createSuccess'));
        onInvoiceCreated(realData, false);
      } catch (error: any) {
        useInvoiceStoreBase.setState((state: any) => ({
          items: state.items.filter((i: any) => i.id !== tempId),
          pagination: state.pagination ? {
            ...state.pagination,
            totalItems: Math.max(0, state.pagination.totalItems - 1),
          } : null,
        }));
        const msg = error.response?.data?.error || error.response?.data?.message || t('common.form.operationFailed');
        toast.error(msg);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-visible"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? t('invoices.edit') : t('invoices.add')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-visible">
              {/* 基本資訊 - Row 1: 發票編號 | 發票日期 | 到期日 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormFieldWithError
                  label={t('invoices.number')}
                  required={true}
                  error={errors.number?.message}
                  htmlFor="number"
                >
                  <Input
                    id="number"
                    type="text"
                    {...register('number')}
                    variant={errors.number ? "error" : "default"}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('invoices.date')}
                  required={true}
                  error={errors.date?.message}
                  htmlFor="date"
                >
                  <Input
                    id="date"
                    type="date"
                    {...register('date')}
                    variant={errors.date ? "error" : "default"}
                    className="cursor-pointer"
                    onClick={(e) => e.currentTarget.showPicker?.()}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('invoices.dueDate')}
                  error={errors.dueDate?.message}
                  htmlFor="dueDate"
                >
                  <Input
                    id="dueDate"
                    type="date"
                    {...register('dueDate')}
                    variant={errors.dueDate ? "error" : "default"}
                    className="cursor-pointer"
                    onClick={(e) => e.currentTarget.showPicker?.()}
                  />
                </FormFieldWithError>
              </div>

              {/* Row 2: 客戶名稱 (2/3) | 統一編號 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <FormFieldWithError
                    label={t('invoices.customerName')}
                    required={true}
                    error={errors.customerName?.message}
                    htmlFor="customerName"
                  >
                    <Input
                      id="customerName"
                      type="text"
                      {...register('customerName')}
                      variant={errors.customerName ? "error" : "default"}
                    />
                  </FormFieldWithError>
                </div>

                <FormFieldWithError
                  label={t('invoices.customerTaxId')}
                  error={errors.customerTaxId?.message}
                  htmlFor="customerTaxId"
                >
                  <Input
                    id="customerTaxId"
                    type="text"
                    {...register('customerTaxId')}
                    variant={errors.customerTaxId ? "error" : "default"}
                  />
                </FormFieldWithError>
              </div>

              {/* Row 3: 客戶地址 | 稅率 | 狀態 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormFieldWithError
                  label={t('invoices.address')}
                  error={errors.customerAddress?.message}
                  htmlFor="customerAddress"
                >
                  <Input
                    id="customerAddress"
                    type="text"
                    {...register('customerAddress')}
                    variant={errors.customerAddress ? "error" : "default"}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('invoices.taxRate')}
                  error={errors.taxRate?.message}
                  htmlFor="taxRate"
                >
                  <Input
                    id="taxRate"
                    type="text"
                    {...register('taxRate', {
                      setValueAs: (value) => {
                        const num = parseFloat(value);
                        return isNaN(num) ? 0 : num;
                      }
                    })}
                    variant={errors.taxRate ? "error" : "default"}
                    placeholder="0.05"
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('common.table.columns.status')}
                  error={errors.status?.message}
                  htmlFor="status"
                >
                  <CustomSelect
                    {...register('status')}
                    value={watch('status')}
                    onChange={(value) => setValue('status', value as 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED')}
                    options={[
                      { value: 'DRAFT', label: t('invoices.status.DRAFT') },
                      { value: 'SENT', label: t('invoices.status.SENT') },
                      { value: 'PAID', label: t('invoices.status.PAID') },
                      { value: 'OVERDUE', label: t('invoices.status.OVERDUE') },
                      { value: 'CANCELLED', label: t('invoices.status.CANCELLED') }
                    ]}
                  />
                </FormFieldWithError>
              </div>

              {/* 發票項目 */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-lg font-medium text-gray-900 dark:text-white">
                    {t('invoices.items')} <span className="text-gray-900 dark:text-white ml-1">*</span>
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddItem}
                    className="inline-flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t('invoices.addItem')}
                  </Button>
                </div>
                
                {errors.items && (
                  <div className="mb-3 text-sm text-red-600 dark:text-red-400">
                    {errors.items.message || (errors.items as any)?.root?.message}
                  </div>
                )}

                {/* 標題行 */}
                <div className="grid grid-cols-12 gap-4 items-center mb-4">
                  <div className="col-span-5">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('invoices.itemDescription')}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('invoices.quantity')}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('invoices.unitPrice')}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('invoices.itemSubtotal')}
                    </span>
                  </div>
                  <div className="col-span-1">
                    {/* 操作列 */}
                  </div>
                </div>

                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-5">
                        <div className="space-y-1">
                          <Input
                            id={`items.${index}.description`}
                            {...register(`items.${index}.description`)}
                            variant={errors.items?.[index]?.description ? "error" : "default"}
                          />
                          {errors.items?.[index]?.description && (
                            <div className="text-xs text-red-600 dark:text-red-400">
                              {errors.items[index].description.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="space-y-1">
                          <Input
                            id={`items.${index}.quantity`}
                            type="text"
                            placeholder="0"
                            {...register(`items.${index}.quantity`, { 
                              setValueAs: (value) => {
                                const num = parseFloat(value);
                                return isNaN(num) ? 0 : num;
                              }
                            })}
                            variant={errors.items?.[index]?.quantity ? "error" : "default"}
                          />
                          {errors.items?.[index]?.quantity && (
                            <div className="text-xs text-red-600 dark:text-red-400">
                              {errors.items[index].quantity.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="space-y-1">
                          <Input
                            id={`items.${index}.unitPrice`}
                            type="text"
                            placeholder="0"
                            {...register(`items.${index}.unitPrice`, { 
                              setValueAs: (value) => {
                                if (value === '' || value === undefined || value === null) {
                                  return '';
                                }
                                const num = parseFloat(value);
                                return isNaN(num) ? '' : num;
                              }
                            })}
                            variant={errors.items?.[index]?.unitPrice ? "error" : "default"}
                          />
                          {errors.items?.[index]?.unitPrice && (
                            <div className="text-xs text-red-600 dark:text-red-400">
                              {errors.items[index].unitPrice.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="py-2 px-3 text-sm text-gray-900 dark:text-white font-mono">
                          ${Math.round((Number(watchedItems[index]?.quantity) || 0) * (Number(watchedItems[index]?.unitPrice) || 0))}
                        </div>
                      </div>
                      <div className="col-span-1 flex items-center">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                          disabled={fields.length <= 1}
                          className="w-full"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 總計顯示 */}
                <div className="mt-6 bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{t('invoices.itemSubtotal')}：</span>
                    <span className="font-mono font-medium">${Math.round(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-gray-600 dark:text-gray-400">{t('invoices.taxAmount')}：</span>
                    <span className="font-mono font-medium">${Math.round(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold border-t border-gray-200 dark:border-slate-600 pt-2 mt-2">
                    <span>{t('invoices.totalAmount')}：</span>
                    <span className="font-mono">${Math.round(totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* 備註 */}
              <FormFieldWithError
                label={t('invoices.notes')}
                error={errors.notes?.message}
                htmlFor="notes"
              >
                <Textarea
                  id="notes"
                  rows={3}
                  {...register('notes')}
                  variant={errors.notes ? "error" : "default"}
                />
              </FormFieldWithError>

          {/* 按鈕 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md"
              onClick={handleClose}
            >
              {t('common.actions.cancel')}
            </button>
            <Button
              type="submit"
              className="inline-flex justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
            >
              {isEditing ? t('common.actions.update') : t('common.actions.add')}
            </Button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default InvoiceDialog;